import { getSupabase, isBackendConfigured } from './supabase';
import { getLocalProfile, putLocalProfile } from './db';
import { profileSchema, type Profile } from './types';

/**
 * Session bootstrap. The plan calls for try-before-signup: a session exists from
 * the first screen so progress is never lost, and it is upgraded to a real
 * account later.
 *
 * Two modes:
 *  - backend configured  → Supabase anonymous auth, profile row created by trigger
 *  - no backend          → a local-only guest profile in IndexedDB
 */

function localTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function newLocalProfile(id: string, localOnly: boolean): Profile {
  return profileSchema.parse({
    id,
    timezone: localTimezone(),
    localOnly,
  });
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `local-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export interface SessionInfo {
  userId: string;
  isAnonymous: boolean;
  email: string | null;
  /** False when running without a backend: progress stays on this device. */
  backed: boolean;
}

/**
 * Ensures a usable session and profile exist, creating them if needed.
 * Safe to call repeatedly; it reuses whatever already exists.
 */
export async function ensureSession(): Promise<{ session: SessionInfo; profile: Profile }> {
  const supabase = await getSupabase();

  if (supabase === null) {
    const existing = await getLocalProfile();
    const profile = existing ?? newLocalProfile(randomId(), true);
    if (!existing) await putLocalProfile(profile);
    return {
      session: { userId: profile.id, isAnonymous: true, email: null, backed: false },
      profile,
    };
  }

  const { data: got } = await supabase.auth.getSession();
  let user = got.session?.user ?? null;

  if (user === null) {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error !== null) {
      // A configured backend that cannot be reached must not stop the app from
      // opening: practicing offline is the point, and a learner on a train has
      // no way to act on a sign-in error. The outbox keeps whatever they do and
      // flushes when the connection returns.
      console.warn(`[auth] sign-in failed, opening offline: ${error.message}`);
      return offlineSession();
    }
    user = data.user;
  }
  if (user === null) return offlineSession();

  const session: SessionInfo = {
    userId: user.id,
    isAnonymous: user.is_anonymous ?? user.email === undefined,
    email: user.email ?? null,
    backed: true,
  };

  const profile = await loadOrCreateProfile(session);
  return { session, profile };
}

/**
 * Opens with whatever this device already has. `backed` is false because
 * nothing has been confirmed with the server this launch; the profile keeps its
 * own `localOnly` flag, so a profile that belongs to a real account is not
 * mislabelled as a guest by one offline start.
 */
async function offlineSession(): Promise<{ session: SessionInfo; profile: Profile }> {
  const existing = await getLocalProfile();
  const profile = existing ?? newLocalProfile(randomId(), true);
  if (existing === undefined) await putLocalProfile(profile);
  return {
    session: { userId: profile.id, isAnonymous: true, email: null, backed: false },
    profile,
  };
}

async function loadOrCreateProfile(session: SessionInfo): Promise<Profile> {
  const supabase = await getSupabase();
  const cached = await getLocalProfile();

  if (supabase === null) {
    const profile = cached ?? newLocalProfile(session.userId, true);
    if (!cached) await putLocalProfile(profile);
    return profile;
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', session.userId)
    .maybeSingle();

  if (error) {
    // Network or policy problem: fall back to the cached copy so the app opens.
    console.warn(`[auth] profile fetch failed, using local copy: ${error.message}`);
    const profile = cached ?? newLocalProfile(session.userId, false);
    if (!cached) await putLocalProfile(profile);
    return profile;
  }

  if (data === null) {
    // The insert trigger has not run yet (or was never installed): create locally
    // and let the settings sync push it.
    const profile = newLocalProfile(session.userId, false);
    await putLocalProfile(profile);
    return profile;
  }

  const profile = mergeProfile(fromRow(data, session), cached);
  await putLocalProfile(profile);
  return profile;
}

/**
 * The conflict policy when a device has both a server row and a local one.
 *
 * The economy is server-authoritative without exception: XP, level, gems,
 * Picks, streak and entitlements always come from the server, because the
 * server computes them and a client figure is only ever a preview.
 *
 * Settings are client-authoritative, and the newer copy wins. This matters
 * because the outbox is asynchronous: a learner who picks an instrument and
 * immediately reloads has a local profile the server has not seen yet, and
 * taking the server's copy would silently undo their choice. Comparing
 * `updatedAt` keeps the change until its sync lands, and prefers the server once
 * another device has moved things on.
 */
export function mergeProfile(server: Profile, local: Profile | undefined): Profile {
  if (local === undefined || local.id !== server.id) return server;

  const localIsNewer = Date.parse(local.updatedAt) > Date.parse(server.updatedAt);
  if (!localIsNewer) return server;

  return {
    ...server,
    // Client-authoritative settings, kept from the newer local copy.
    displayName: local.displayName,
    companionCharacter: local.companionCharacter,
    primaryInstrument: local.primaryInstrument,
    instruments: local.instruments,
    timezone: local.timezone,
    dailyGoalXp: local.dailyGoalXp,
    reminderHour: local.reminderHour,
    flags: local.flags,
    updatedAt: local.updatedAt,
  };
}

/** Map a `profiles` row (snake_case) onto the local camelCase shape. */
function fromRow(row: Record<string, unknown>, session: SessionInfo): Profile {
  return profileSchema.parse({
    id: String(row.id ?? session.userId),
    username: (row.username as string | null) ?? null,
    displayName: (row.display_name as string | null) ?? null,
    isAnonymous: (row.is_anonymous as boolean | undefined) ?? session.isAnonymous,
    companionCharacter: row.companion_character ?? 'tempo',
    primaryInstrument: row.primary_instrument ?? 'ukulele',
    instruments: row.instruments ?? ['ukulele'],
    timezone: (row.timezone as string | undefined) ?? localTimezone(),
    dailyGoalXp: row.daily_goal_xp ?? 20,
    level: row.level ?? 1,
    totalXp: Number(row.total_xp ?? 0),
    gems: row.gems ?? 0,
    streakCurrent: row.streak_current ?? 0,
    streakLongest: row.streak_longest ?? 0,
    streakLastDate: (row.streak_last_date as string | null) ?? null,
    streakRestUsedOn: (row.streak_rest_used_on as string | null) ?? null,
    isPremium: row.is_premium ?? false,
    contentVersion: row.content_version ?? 1,
    reminderHour: (row.reminder_hour as number | null) ?? null,
    // `undefined` rather than `null` so a row that predates the column falls
    // through to the schema's defaults. Leaving this out entirely used to wipe
    // onboarding's answers: the settings sync pushes `flags` up and stamps a
    // newer `updated_at`, so on the next launch `mergeProfile` prefers the
    // server copy — and the server copy came back with every flag defaulted.
    // A learner lost their microphone opt-in, their experience level, their
    // placement result and the minor flag by reloading the page.
    flags: row.flags ?? undefined,
    updatedAt: (row.updated_at as string | undefined) ?? new Date().toISOString(),
    localOnly: false,
  });
}

/* ------------------------------------------------------------ upgrade flows */

export async function signUpWithEmail(email: string, password: string): Promise<void> {
  const supabase = await getSupabase();
  if (supabase === null) throw new Error('No backend configured');

  // An anonymous user is upgraded in place so their progress carries over.
  const { data: got } = await supabase.auth.getSession();
  const isAnon = got.session?.user.is_anonymous === true;

  if (isAnon) {
    const { error } = await supabase.auth.updateUser({ email, password });
    if (error) throw new Error(error.message);
    return;
  }

  const { error } = await supabase.auth.signUp({ email, password });
  if (error) throw new Error(error.message);
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  const supabase = await getSupabase();
  if (supabase === null) throw new Error('No backend configured');
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  const supabase = await getSupabase();
  if (supabase === null) return;
  await supabase.auth.signOut();
}

export { isBackendConfigured };

export const __testables = { fromRow };
